import Button from "components/Buttons/Button";
import React from "react";
import { ReactComponent as TransferWalletIcon } from "assets/TransferWallet.svg";
import InputField from "components/FormFields/InputField";
import { PiGiftFill } from "react-icons/pi";
import { useFormik } from "formik";
import * as Yup from "yup";

interface ITransferConfirmationModalProps {
  onTransfer: (values: ITransferFormValues) => void;
  onCancel: () => void;
  walletBalance?: number;
  transferringAmount: boolean;
}

export interface ITransferFormValues {
  transferAmount: number | null;
}

const TransferConfirmationModal: React.FC<ITransferConfirmationModalProps> = ({
  onTransfer,
  onCancel,
  transferringAmount,
  walletBalance,
}) => {
  const validationSchema = Yup.object({
    transferAmount: Yup.number()
      .typeError("Amount must be a number")
      .required("Amount is required")
      .moreThan(0, "Amount must be greater than zero")
      .max(walletBalance ?? 0, "Amount cannot exceed your wallet balance"),
  });

  const formik = useFormik({
    initialValues: { transferAmount: null },
    validationSchema,
    onSubmit: async (values: ITransferFormValues) => {
      onTransfer(values);
    },
  });

  const { handleSubmit, values, errors, setFieldValue, touched } = formik;

  return (
    <div className="flex flex-col h-full flex-1">
      <div className="flex-1 gap-4 flex flex-col mb-6 items-center">
        <TransferWalletIcon className="inline-block h-18 w-18" />
        <div className="font-semibold text-black text-center md:text-xl">
          Transfer rewards fund balance?
        </div>
        <div className="md:text-sm text-xs text-center text-opacity-80">
          Please enter the amount you'd like to transfer from your available
          wallet balance.
        </div>
        <div className="bg-lightPurple flex p-3 w-full items-center">
          <PiGiftFill size={20} color="#750ACF" className="mr-2" />
          <div className="flex flex-1 text-secondary font-medium text-sm">
            Available Balance
          </div>
          <div className="text-secondary font-medium text-sm">
            ${walletBalance}
          </div>
        </div>
        <div className="self-start w-full">
          <form onSubmit={handleSubmit} id="transfer-balance-form">
            <InputField
              type={"number"}
              id="transfer-amount"
              value={values?.transferAmount}
              label={"Transfer Amount"}
              placeholder="Enter Transfer Amount"
              onChange={(e) => {
                setFieldValue("transferAmount", e.target.value);
              }}
              error={touched.transferAmount && errors?.transferAmount}
              // To allow decimal values submission
              extras={{ step: "any" }}
            />
          </form>
        </div>
      </div>

      <div className="flex md:flex-row flex-col-reverse w-full self-center justify-center gap-4 no-scrollbar">
        <Button label={"Cancel"} onClick={onCancel} variant="secondary" />
        <Button
          form="transfer-balance-form"
          label={"Initiate transfer"}
          type="submit"
          loading={transferringAmount}
        />
      </div>
    </div>
  );
};

export default TransferConfirmationModal;
